import React, { useState, useEffect } from 'react';
import SearchableSelect from './SearchableSelect';
import './WorkItemForm.css';

const WorkItemForm = ({ workItem, isEditing, onSubmit, onCancel, clientes, tiposCliente, users, canAssign }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    dueDate: '',
    clienteId: '',
    tipoClienteId: '',
    assignedTo: '',
    selectedServicos: [],
    customActivities: [],
    reembolsos: [],
    totalValue: 0,
    totalConsolidado: 0,
    authorized: true
  });

  const [availableServicos, setAvailableServicos] = useState([]);
  const [newActivity, setNewActivity] = useState({ nome: '', valor: '' });
  const [newReembolso, setNewReembolso] = useState({ descricao: '', valor: '' });

  // Initialize form data
  useEffect(() => {
    if (workItem && isEditing) {
      // Extract IDs from various possible sources
      // The backend might return clienteId as string, object, or in clienteData
      let clienteId = '';
      if (typeof workItem.clienteId === 'string') {
        clienteId = workItem.clienteId;
      } else if (workItem.clienteId?._id) {
        clienteId = workItem.clienteId._id;
      } else if (workItem.clienteData?._id) {
        clienteId = workItem.clienteData._id;
      }
      
      let tipoClienteId = '';
      if (typeof workItem.tipoClienteId === 'string') {
        tipoClienteId = workItem.tipoClienteId;
      } else if (workItem.tipoClienteId?._id) {
        tipoClienteId = workItem.tipoClienteId._id;
      } else if (workItem.tipoClienteData?._id) {
        tipoClienteId = workItem.tipoClienteData._id;
      }
      
      let assignedToId = '';
      if (typeof workItem.assignedTo === 'string') {
        assignedToId = workItem.assignedTo;
      } else if (workItem.assignedTo?._id) {
        assignedToId = workItem.assignedTo._id;
      } else if (workItem.assignedToData?._id) {
        assignedToId = workItem.assignedToData._id;
      }
      
      setFormData({
        title: workItem.title || '',
        description: workItem.description || '',
        status: workItem.status || 'todo',
        priority: workItem.priority || 'medium',
        dueDate: workItem.dueDate ? workItem.dueDate.split('T')[0] : '',
        clienteId: clienteId,
        tipoClienteId: tipoClienteId,
        assignedTo: assignedToId,
        selectedServicos: workItem.selectedServicos || [],
        customActivities: workItem.customActivities || [],
        reembolsos: workItem.reembolsos || [],
        totalValue: workItem.totalValue || 0,
        totalConsolidado: workItem.totalConsolidado || 0,
        authorized: workItem.authorized !== undefined ? workItem.authorized : true
      });
    } else if (!isEditing) {
      // Reset form when creating new item
      setFormData({
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        dueDate: '',
        clienteId: '',
        tipoClienteId: '',
        assignedTo: '',
        selectedServicos: [],
        customActivities: [],
        reembolsos: [],
        totalValue: 0,
        totalConsolidado: 0,
        authorized: true
      });
    }
  }, [workItem, isEditing]);

  // Update available services when tipo cliente changes
  useEffect(() => {
    if (formData.tipoClienteId) {
      const selectedTipo = tiposCliente.find(tipo => tipo._id === formData.tipoClienteId);
      if (selectedTipo && selectedTipo.servicos) {
        setAvailableServicos(selectedTipo.servicos);
      } else {
        setAvailableServicos([]);
      }
    } else {
      setAvailableServicos([]);
    }
  }, [formData.tipoClienteId, tiposCliente]);

  // Calculate total values
  useEffect(() => {
    const servicosTotal = formData.selectedServicos.reduce((sum, servico) => sum + (servico.valor || 0), 0);
    const servicosConsolidado = formData.selectedServicos
      .filter(servico => servico.completed)
      .reduce((sum, servico) => sum + (servico.valor || 0), 0);
    
    const activitiesTotal = formData.customActivities.reduce((sum, activity) => sum + (activity.valor || 0), 0);
    const activitiesConsolidado = formData.customActivities
      .filter(activity => activity.completed)
      .reduce((sum, activity) => sum + (activity.valor || 0), 0);
    
    const reembolsosTotal = formData.reembolsos.reduce((sum, reembolso) => sum + (reembolso.valor || 0), 0);
    const reembolsosConsolidado = formData.reembolsos
      .filter(reembolso => reembolso.completed)
      .reduce((sum, reembolso) => sum + (reembolso.valor || 0), 0);
    
    const totalPrevisto = servicosTotal + activitiesTotal + reembolsosTotal;
    const totalConsolidado = servicosConsolidado + activitiesConsolidado + reembolsosConsolidado;
    
    setFormData(prev => ({ 
      ...prev, 
      totalValue: totalPrevisto,
      totalConsolidado: totalConsolidado 
    }));
  }, [formData.selectedServicos, formData.customActivities, formData.reembolsos]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServicoToggle = (servico) => {
    setFormData(prev => {
      const isSelected = prev.selectedServicos.some(s => s.nome === servico.nome);
      
      if (isSelected) {
        return {
          ...prev,
          selectedServicos: prev.selectedServicos.filter(s => s.nome !== servico.nome)
        };
      } else {
        return {
          ...prev,
          selectedServicos: [...prev.selectedServicos, { ...servico, fromTipoCliente: true, completed: false }]
        };
      }
    });
  };

  const handleServicoCompletedToggle = (servicoNome) => {
    if (!formData.authorized) {
      alert('Este item precisa ser autorizado pelo cliente antes de marcar serviços como concluídos.');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      selectedServicos: prev.selectedServicos.map(s => 
        s.nome === servicoNome ? { ...s, completed: !s.completed } : s
      )
    }));
  };

  const handleAddActivity = () => {
    if (!newActivity.nome || !newActivity.valor) {
      alert('Por favor, preencha o nome e valor da atividade');
      return;
    }

    const activity = {
      id: Date.now().toString(),
      nome: newActivity.nome,
      valor: parseFloat(newActivity.valor),
      fromTipoCliente: false,
      completed: false
    };

    setFormData(prev => ({
      ...prev,
      customActivities: [...prev.customActivities, activity]
    }));

    setNewActivity({ nome: '', valor: '' });
  };

  const handleRemoveActivity = (id) => {
    setFormData(prev => ({
      ...prev,
      customActivities: prev.customActivities.filter(a => a.id !== id)
    }));
  };

  const handleActivityCompletedToggle = (activityId) => {
    if (!formData.authorized) {
      alert('Este item precisa ser autorizado pelo cliente antes de marcar atividades como concluídas.');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      customActivities: prev.customActivities.map(a => 
        a.id === activityId ? { ...a, completed: !a.completed } : a
      )
    }));
  };

  const handleAddReembolso = () => {
    if (!newReembolso.descricao || !newReembolso.valor) {
      alert('Por favor, preencha a descrição e valor do reembolso');
      return;
    }

    const reembolso = {
      id: Date.now().toString(),
      descricao: newReembolso.descricao,
      valor: parseFloat(newReembolso.valor),
      completed: false
    };

    setFormData(prev => ({
      ...prev,
      reembolsos: [...prev.reembolsos, reembolso]
    }));

    setNewReembolso({ descricao: '', valor: '' });
  };

  const handleRemoveReembolso = (id) => {
    setFormData(prev => ({
      ...prev,
      reembolsos: prev.reembolsos.filter(r => r.id !== id)
    }));
  };

  const handleReembolsoCompletedToggle = (reembolsoId) => {
    if (!formData.authorized) {
      alert('Este item precisa ser autorizado pelo cliente antes de marcar reembolsos como concluídos.');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      reembolsos: prev.reembolsos.map(r => 
        r.id === reembolsoId ? { ...r, completed: !r.completed } : r
      )
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.title || !formData.clienteId) {
      alert('Por favor, preencha o título e selecione um cliente');
      return;
    }

    // Prepare submission data - ensure we only send IDs, not full objects
    const submissionData = {
      title: formData.title,
      description: formData.description,
      status: formData.status,
      priority: formData.priority,
      dueDate: formData.dueDate,
      clienteId: formData.clienteId,
      tipoClienteId: formData.tipoClienteId,
      assignedTo: formData.assignedTo,
      selectedServicos: formData.selectedServicos,
      customActivities: formData.customActivities,
      reembolsos: formData.reembolsos,
      totalValue: formData.totalValue,
      totalConsolidado: formData.totalConsolidado
    };

    onSubmit(submissionData);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Calculate progress
  const totalItems = formData.selectedServicos.length + 
                     formData.customActivities.length + 
                     formData.reembolsos.length;
  const completedItems = formData.selectedServicos.filter(s => s.completed).length +
                        formData.customActivities.filter(a => a.completed).length +
                        formData.reembolsos.filter(r => r.completed).length;
  const progressPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <form className="work-item-form" onSubmit={handleSubmit}>
      {isEditing && !formData.authorized && (
        <div className="authorization-warning-form">
          <div className="warning-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="5" y="11" width="14" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>
          <div className="warning-content">
            <h4>Aguardando Autorização do Cliente</h4>
            <p>Este item precisa ser autorizado pelo cliente antes que você possa marcar atividades como concluídas.</p>
          </div>
        </div>
      )}
      
      <div className="form-section">
        <h3>Informações Básicas</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label htmlFor="title">Título *</label>
            <input
              type="text"
              id="title"
              name="title"
              className="form-control"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Título do item de trabalho"
              required
            />
          </div>

          <div className="form-group full-width">
            <label htmlFor="description">Descrição</label>
            <textarea
              id="description"
              name="description"
              className="form-control"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Descrição detalhada do trabalho"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              className="form-control"
              value={formData.status}
              onChange={handleInputChange}
            >
              <option value="todo">A Fazer</option>
              <option value="inProgress">Em Progresso</option>
              <option value="done">Concluído</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="priority">Prioridade</label>
            <select
              id="priority"
              name="priority"
              className="form-control"
              value={formData.priority}
              onChange={handleInputChange}
            >
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="dueDate">Data de Vencimento</label>
            <input
              type="date"
              id="dueDate"
              name="dueDate"
              className="form-control"
              value={formData.dueDate}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Cliente e Tipo</h3>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="clienteId">Cliente *</label>
            <SearchableSelect
              options={clientes}
              value={formData.clienteId}
              onChange={(value) => setFormData(prev => ({ ...prev, clienteId: value }))}
              placeholder="Selecione um cliente"
              displayField={(cliente) => cliente.nomeFantasia || cliente.razaoSocial || ''}
              required={true}
            />
          </div>

          <div className="form-group">
            <label htmlFor="tipoClienteId">Tipo de Cliente</label>
            <SearchableSelect
              options={tiposCliente}
              value={formData.tipoClienteId}
              onChange={(value) => setFormData(prev => ({ ...prev, tipoClienteId: value }))}
              placeholder="Selecione um tipo"
              displayField="nome"
              required={false}
            />
          </div>

          {canAssign && users.length > 0 && (
            <div className="form-group">
              <label htmlFor="assignedTo">Responsável</label>
              <SearchableSelect
                options={users}
                value={formData.assignedTo}
                onChange={(value) => setFormData(prev => ({ ...prev, assignedTo: value }))}
                placeholder="Selecione um responsável (padrão: você)"
                displayField="nomeCompleto"
                required={false}
              />
            </div>
          )}
        </div>
      </div>

      {availableServicos.length > 0 && (
        <div className="form-section">
          <h3>Serviços do Tipo de Cliente</h3>
          <div className="servicos-list">
            {availableServicos.map((servico, index) => {
              const isSelected = formData.selectedServicos.some(s => s.nome === servico.nome);
              const selectedServico = formData.selectedServicos.find(s => s.nome === servico.nome);
              
              return (
                <div key={index} className="servico-item-wrapper">
                  <label className="servico-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleServicoToggle(servico)}
                    />
                    <span className="servico-info">
                      <span>{servico.nome}</span>
                      <span className="servico-valor">{formatCurrency(servico.valor)}</span>
                    </span>
                  </label>
                  {isSelected && (
                    <label className="completed-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedServico?.completed || false}
                        onChange={() => handleServicoCompletedToggle(servico.nome)}
                        disabled={!formData.authorized}
                        title={!formData.authorized ? "Item precisa ser autorizado pelo cliente" : ""}
                      />
                      <span>Concluído</span>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="form-section">
        <h3>Atividades Adicionais</h3>
        <div className="add-item-section">
          <div className="add-item-form">
            <input
              type="text"
              className="form-control"
              placeholder="Nome da atividade"
              value={newActivity.nome}
              onChange={(e) => setNewActivity({ ...newActivity, nome: e.target.value })}
            />
            <input
              type="number"
              className="form-control"
              placeholder="Valor"
              value={newActivity.valor}
              onChange={(e) => setNewActivity({ ...newActivity, valor: e.target.value })}
              step="0.01"
              min="0"
            />
            <button type="button" className="btn-add" onClick={handleAddActivity}>
              Adicionar
            </button>
          </div>

          {formData.customActivities.length > 0 && (
            <div className="items-list">
              {formData.customActivities.map(activity => (
                <div key={activity.id} className="item-row with-checkbox">
                  <label className="completed-checkbox">
                    <input
                      type="checkbox"
                      checked={activity.completed || false}
                      onChange={() => handleActivityCompletedToggle(activity.id)}
                      disabled={!formData.authorized}
                      title={!formData.authorized ? "Item precisa ser autorizado pelo cliente" : ""}
                    />
                  </label>
                  <span className={activity.completed ? 'completed' : ''}>{activity.nome}</span>
                  <span className={activity.completed ? 'completed' : ''}>{formatCurrency(activity.valor)}</span>
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => handleRemoveActivity(activity.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="form-section">
        <h3>Reembolsos</h3>
        <div className="add-item-section">
          <div className="add-item-form">
            <input
              type="text"
              className="form-control"
              placeholder="Descrição do reembolso"
              value={newReembolso.descricao}
              onChange={(e) => setNewReembolso({ ...newReembolso, descricao: e.target.value })}
            />
            <input
              type="number"
              className="form-control"
              placeholder="Valor"
              value={newReembolso.valor}
              onChange={(e) => setNewReembolso({ ...newReembolso, valor: e.target.value })}
              step="0.01"
              min="0"
            />
            <button type="button" className="btn-add" onClick={handleAddReembolso}>
              Adicionar
            </button>
          </div>

          {formData.reembolsos.length > 0 && (
            <div className="items-list">
              {formData.reembolsos.map(reembolso => (
                <div key={reembolso.id} className="item-row with-checkbox">
                  <label className="completed-checkbox">
                    <input
                      type="checkbox"
                      checked={reembolso.completed || false}
                      onChange={() => handleReembolsoCompletedToggle(reembolso.id)}
                      disabled={!formData.authorized}
                      title={!formData.authorized ? "Item precisa ser autorizado pelo cliente" : ""}
                    />
                  </label>
                  <span className={reembolso.completed ? 'completed' : ''}>{reembolso.descricao}</span>
                  <span className={reembolso.completed ? 'completed' : ''}>{formatCurrency(reembolso.valor)}</span>
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => handleRemoveReembolso(reembolso.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {totalItems > 0 && (
        <div className="form-section progress-section">
          <h3>Progresso</h3>
          <div className="progress-info">
            <span>{completedItems} de {totalItems} atividades concluídas ({progressPercentage}%)</span>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="form-section total-section">
        <h3>Valores</h3>
        <div className="totals-container">
          <div className="total-item">
            <span className="total-label">Total Previsto:</span>
            <span className="total-value previsto">{formatCurrency(formData.totalValue)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Total Consolidado:</span>
            <span className="total-value consolidado">{formatCurrency(formData.totalConsolidado)}</span>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn-cancel" onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" className="btn-submit">
          {isEditing ? 'Salvar Alterações' : 'Criar Item'}
        </button>
      </div>
    </form>
  );
};

export default WorkItemForm;
